/**
 * @file ActivityDashboard.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-28
 * @modified 2026-10-21
 * @description Admin-only cross-entity audit log search.
 *              Backed by /api/audit/search, /api/audit/entity-types, /api/audit/actions.
 *
 *              1.4.0 (Cam, 21 Oct 2026 — "The admin activity log is not showing
 *              details that it should regarding costing and pricing activity"):
 *              the details are the history window's own renderer
 *              (renderAuditChanges → utils/auditDetails), not a second copy that
 *              read only { old, new } pairs — so Pricing & Costing confirmations,
 *              rep prices, price requests, contracts from rep replies and the
 *              cost sent to Xero show what was done. The product / PO an entry
 *              is about sits under its identifier, the record opens from it, and
 *              a price request opens the email that went to the rep.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './ActivityDashboard.css';
import Input from './Input';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import LoadingSpinner from './LoadingSpinner';
import PaginationControls from './PaginationControls';
import { useSlideOutButton } from './SlideOutButton';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import SentEmailModal from './SentEmailModal';
import { useEntityModal } from './EntityModalProvider';
import { contextOf, emailIdOf, openIdOf } from '../utils/auditDetails';
import {
  countChangeRows,
  entrySource,
  formatDate,
  getActionInfo,
  getEntityTypeLabel,
  renderAuditChanges
} from './AuditHistoryModal';

const PAGE_SIZE_OPTIONS = [
  { value: '20', label: '20' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' }
];

const TABLE_COL_COUNT = 7;

// What Clear Filters resets to (limit is kept, and never counts as a filter).
const CLEARED_FILTERS = {
  entityType: '', action: '', userId: '', dateFrom: '', dateTo: '', search: '', limit: '20'
};

function ActivityDashboard() {
  // An EMAIL_SENT row opens the email as it was sent (SentEmailModal).
  const [viewEmailEntry, setViewEmailEntry] = useState(null);
  // A price request's tracked email (audit.email_messages id).
  const [viewEmailId, setViewEmailId] = useState(null);
  const { openEntity, available: canOpen } = useEntityModal();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [actions, setActions] = useState([]);

  const [filters, setFilters] = useState(CLEARED_FILTERS);
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useSlideOutButton();

  // Set the browser tab title while on this page; restore on unmount.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Audit History: Activity';
    return () => { document.title = previous; };
  }, []);

  // Load filter dropdown sources once (admin endpoints)
  useEffect(() => {
    (async () => {
      try {
        const [typesRes, actionsRes, usersRes] = await Promise.all([
          fetch(`${API_URL}/api/audit/entity-types`, { credentials: 'include' }),
          fetch(`${API_URL}/api/audit/actions`, { credentials: 'include' }),
          fetch(`${API_URL}/api/users/admin/users`, { credentials: 'include' })
        ]);
        if (typesRes.ok) setEntityTypes(await typesRes.json());
        if (actionsRes.ok) setActions(await actionsRes.json());
        if (usersRes.ok) {
          const data = await usersRes.json();
          setUsers(data.users || []);
        }
      } catch (e) {
        console.error('Failed to load activity filter sources:', e);
      }
    })();
  }, []);

  // Responses come back out of order; only the newest may write the rows.
  const latest = useLatestRequest();

  const loadLogs = async (targetPage = page) => {
    const seq = latest.begin();
    setLoading(true);
    setError(null);
    try {
      const limit = parseInt(filters.limit, 10);
      const offset = (targetPage - 1) * limit;
      const params = new URLSearchParams({ limit, offset });
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.action) params.set('action', filters.action);
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.dateFrom) params.set('startDate', filters.dateFrom);
      if (filters.dateTo) params.set('endDate', filters.dateTo);
      if (filters.search) params.set('search', filters.search);

      const res = await fetch(`${API_URL}/api/audit/search?${params}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Admin access required');
        throw new Error('Failed to load activity log');
      }
      const data = await res.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (!latest.isLatest(seq)) return;
      console.error('Activity load error:', e);
      setError(e.message);
      setLogs([]);
      setTotal(0);
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
  };

  // Reload when filters change (debounced for the free-text search input)
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setExpandedRows(new Set());
      loadLogs(1);
    }, filters.search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.entityType, filters.action, filters.userId, filters.dateFrom, filters.dateTo, filters.search, filters.limit]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const limitNum = parseInt(filters.limit, 10);
  const totalPages = Math.max(1, Math.ceil(total / limitNum));
  const pagination = { page, limit: limitNum, total, totalPages };

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    setExpandedRows(new Set());
    loadLogs(newPage);
  };

  const entityTypeOptions = useMemo(() => ([
    { value: '', label: 'All Entities' },
    ...entityTypes.map(t => ({ value: t, label: getEntityTypeLabel(t) }))
  ]), [entityTypes]);

  const actionOptions = useMemo(() => ([
    { value: '', label: 'All Actions' },
    ...actions.map(a => ({ value: a, label: getActionInfo(a).label }))
  ]), [actions]);

  const userOptions = useMemo(() => ([
    { value: '', label: 'All Users' },
    ...users.map(u => ({
      value: String(u.id),
      label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
    }))
  ]), [users]);

  const toggleRowExpand = (id) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleExpandAll = () => {
    if (expandedRows.size === logs.length) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(logs.map(l => l.id)));
    }
  };

  const clearFilters = () => setFilters({ ...CLEARED_FILTERS, limit: filters.limit });

  // The history window's renderer: every field, whatever its shape.
  const renderChanges = (entry) => {
    const rendered = renderAuditChanges(entry.changes, entry.action, entry.resolved, entry.entity_type);
    if (rendered) return rendered;
    return <div className="no-changes">{entry.action === 'DELETE' ? 'Record deleted' : 'Record created'}</div>;
  };

  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, {
    labels: { entityType: 'Entity type', userId: 'User' }
  });
  const hasActiveFilters = headerFilters.count > 0;

  const allExpanded = logs.length > 0 && expandedRows.size === logs.length;

  return (
    <div className="activity-dashboard">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Cross-entity activity log. Every create, update, status change, archive, restore, login, and sync is recorded here.
          </span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              {total.toLocaleString()} record{total !== 1 ? 's' : ''} match{total === 1 ? 'es' : ''} the current filters
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                onClick={clearFilters}
                className={clearFiltersClass(hasActiveFilters)}
                disabled={!hasActiveFilters || loading}
                title={hasActiveFilters ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button
                onClick={() => loadLogs(page)}
                className="btn-slideout"
                disabled={loading}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">refresh</span>
                </div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['record', 'user', 'where in the system (e.g. Pricing & Costing, Cost from bill)']} />
            </label>
            <Input
              type="text"
              placeholder="Record, user or where (e.g. Pricing & Costing)..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onEnter={() => { setPage(1); setExpandedRows(new Set()); loadLogs(1); }}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Entity</label>
            <Dropdown
              value={filters.entityType}
              onChange={(value) => handleFilterChange('entityType', value)}
              options={entityTypeOptions}
              placeholder="All Entities"
            />
          </div>

          <div className="filter-group">
            <label>Action</label>
            <Dropdown
              value={filters.action}
              onChange={(value) => handleFilterChange('action', value)}
              options={actionOptions}
              placeholder="All Actions"
            />
          </div>

          <div className="filter-group">
            <label>User</label>
            <Dropdown
              value={filters.userId}
              onChange={(value) => handleFilterChange('userId', value)}
              options={userOptions}
              placeholder="All Users"
            />
          </div>

          <div className="filter-group">
            <DateInput
              label="From Date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <DateInput
              label="To Date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={PAGE_SIZE_OPTIONS}
              placeholder="20"
            />
          </div>
        </div>
      </div>

      <div className="activity-panel">
        <div className="table-toolbar">
          <button
            className="btn-secondary btn-sm"
            onClick={handleExpandAll}
            disabled={loading || logs.length === 0}
          >
            <span className="material-symbols-rounded">
              {allExpanded ? 'unfold_less' : 'unfold_more'}
            </span>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>

          {pagination.total > 0 && (
            <PaginationControls pagination={pagination} onPageChange={changePage} />
          )}
        </div>

        <div className="table-container">
          <table className="data-table activity-table">
            <thead>
              <tr>
                <th style={{ width: '34px' }}></th>
                <th style={{ width: '160px' }}>Date</th>
                <th style={{ width: '130px' }}>Action</th>
                <th style={{ width: '160px' }}>Entity</th>
                <th>Identifier</th>
                <th style={{ width: '180px' }}>User</th>
                <th style={{ width: '100px', textAlign: 'right' }}>Fields</th>
              </tr>
            </thead>
            <tbody className={loading ? 'loading-fade' : ''}>
              {logs.length === 0 && !loading && !error ? (
                <tr>
                  <td colSpan={TABLE_COL_COUNT} className="no-results">
                    No activity matches the selected filters.
                  </td>
                </tr>
              ) : logs.length === 0 && loading ? (
                <tr>
                  <td colSpan={TABLE_COL_COUNT}>
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={TABLE_COL_COUNT} className="activity-error">
                    <span className="material-symbols-rounded">error</span>
                    <span>{error}</span>
                  </td>
                </tr>
              ) : (
                logs.map((entry) => {
                  const actionInfo = getActionInfo(entry.action);
                  const isExpanded = expandedRows.has(entry.id);
                  const isCreateOrDelete = entry.action === 'CREATE' || entry.action === 'DELETE';
                  const changesCount = isCreateOrDelete ? null : countChangeRows(entry.changes, entry.action, entry.entity_type);
                  const context = contextOf(entry);
                  const openId = canOpen ? openIdOf(entry) : null;
                  const requestEmailId = emailIdOf(entry);

                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        className="expandable-row"
                        onClick={() => toggleRowExpand(entry.id)}
                      >
                        <td className="expand-cell">
                          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                        </td>
                        <td>{formatDate(entry.created_at)}</td>
                        <td>
                          <span className={`action-badge ${actionInfo.className}`}>
                            {actionInfo.label}
                          </span>
                        </td>
                        <td>
                          <span className="entry-entity-type">
                            {getEntityTypeLabel(entry.entity_type)}
                          </span>
                        </td>
                        <td>
                          {openId != null && entry.entity_identifier ? (
                            <button
                              type="button"
                              className="entry-identifier-link"
                              title={`Open ${entry.entity_identifier}`}
                              onClick={(e) => { e.stopPropagation(); openEntity(entry.entity_type, openId); }}
                            >
                              {entry.entity_identifier}
                            </button>
                          ) : (
                            <span className="entry-identifier">
                              {entry.entity_identifier || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </span>
                          )}
                          {context && <span className="entry-context">{context}</span>}
                        </td>
                        <td>
                          <span className="entry-user-cell">
                            <span className="material-symbols-rounded">person</span>
                            <span>{entry.user_name || 'System'}</span>
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {changesCount === null ? (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          ) : (
                            <span className="changes-count">
                              {changesCount}
                            </span>
                          )}
                        </td>
                      </tr>
                      <tr className={`expanded-content ${isExpanded ? 'expanded' : ''}`}>
                        <td colSpan={TABLE_COL_COUNT}>
                          <div className="line-items-container">
                            <div className="activity-changes-wrapper">
                              {entry.action === 'EMAIL_SENT' && (
                                <button type="button" className="btn-icon activity-view-email"
                                  title="View email as sent" aria-label="View email as sent"
                                  onClick={(e) => { e.stopPropagation(); setViewEmailEntry(entry.id); }}>
                                  <span className="material-symbols-rounded">mail</span>
                                </button>
                              )}
                              {requestEmailId != null && (
                                <button type="button" className="btn-icon activity-view-email"
                                  title="View the price request email as it was sent"
                                  aria-label="View the price request email as it was sent"
                                  onClick={(e) => { e.stopPropagation(); setViewEmailId(requestEmailId); }}>
                                  <span className="material-symbols-rounded">mail</span>
                                </button>
                              )}
                              {entrySource(entry) && (
                                <div className="activity-where">
                                  <span className="material-symbols-rounded">location_on</span>
                                  {entrySource(entry)}
                                </div>
                              )}
                              {renderChanges(entry)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <SentEmailModal isOpen={viewEmailEntry != null} onClose={() => setViewEmailEntry(null)} changeLogId={viewEmailEntry} />
      <SentEmailModal isOpen={viewEmailId != null} onClose={() => setViewEmailId(null)} emailId={viewEmailId} />
    </div>
  );
}

export default ActivityDashboard;
